import { loadRuns, saveRuns, type ValidateRun } from './validate-runs';
import {
  MAX_JUDGES,
  parseJudge,
  withRubric,
  type ValidateJudge,
} from './validate-judges';
import {
  parseSuite,
  parseSuiteRun,
  type SuiteRun,
  type ValidateSuite,
} from './validate-suites';

/**
 * Where Validate keeps its runs, suites and suite runs.
 *
 * Today that is this browser's localStorage, per solution — Pro Studio has no
 * backend store for them yet. The page only talks to this interface, so a
 * real store (Hasura tables, shared across people) can replace
 * `localValidateStore` without touching the page.
 */
export interface ValidateStore {
  loadRuns(solutionKey: string): ValidateRun[];
  saveRuns(solutionKey: string, runs: ValidateRun[]): void;
  loadSuites(solutionKey: string): ValidateSuite[];
  saveSuites(solutionKey: string, suites: ValidateSuite[]): void;
  loadSuiteRuns(solutionKey: string): SuiteRun[];
  saveSuiteRuns(solutionKey: string, suiteRuns: SuiteRun[]): void;
  /** The solution's judges; the defaults when it has none saved. */
  loadJudges(solutionKey: string): ValidateJudge[];
  saveJudges(solutionKey: string, judges: ValidateJudge[]): void;
}

/** Oldest suite runs drop off past this. */
export const MAX_SUITE_RUNS = 30;
export const MAX_SUITES = 50;

type KeyValue = Pick<Storage, 'getItem' | 'setItem'>;

function safeStorage(): KeyValue | null {
  try {
    return typeof window === 'undefined' ? null : window.localStorage;
  } catch {
    return null;
  }
}

const readList = <T>({
  storage,
  key,
  parse,
  max,
}: {
  storage: KeyValue | null;
  key: string;
  parse: (v: unknown) => T | null;
  max: number;
}): T[] => {
  try {
    const raw = storage?.getItem(key);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed)
      ? parsed
          .map(parse)
          .filter((x): x is T => x !== null)
          .slice(0, max)
      : [];
  } catch {
    return [];
  }
};

const writeList = <T>({
  storage,
  key,
  list,
  max,
}: {
  storage: KeyValue | null;
  key: string;
  list: T[];
  max: number;
}) => {
  try {
    storage?.setItem(key, JSON.stringify(list.slice(0, max)));
  } catch {
    // Full or blocked storage: it still shows for this visit.
  }
};

export const suitesKey = (solutionKey: string) =>
  `pro-studio:validate-suites:${solutionKey}`;
export const suiteRunsKey = (solutionKey: string) =>
  `pro-studio:validate-suite-runs:${solutionKey}`;

export const judgesKey = (solutionKey: string) =>
  `pro-studio:validate-judges:${solutionKey}`;

export const createLocalValidateStore = (
  storage: KeyValue | null = safeStorage()
): ValidateStore => ({
  loadRuns: (key) => loadRuns(key, storage),
  saveRuns: (key, runs) => saveRuns(key, runs, storage),
  loadSuites: (key) =>
    readList({
      storage,
      key: suitesKey(key),
      parse: parseSuite,
      max: MAX_SUITES,
    }),
  saveSuites: (key, suites) =>
    writeList({ storage, key: suitesKey(key), list: suites, max: MAX_SUITES }),
  loadSuiteRuns: (key) =>
    readList({
      storage,
      key: suiteRunsKey(key),
      parse: parseSuiteRun,
      max: MAX_SUITE_RUNS,
    }),
  saveSuiteRuns: (key, suiteRuns) =>
    writeList({
      storage,
      key: suiteRunsKey(key),
      list: suiteRuns,
      max: MAX_SUITE_RUNS,
    }),
  loadJudges: (key) =>
    withRubric(
      readList({
        storage,
        key: judgesKey(key),
        parse: parseJudge,
        max: MAX_JUDGES,
      }),
      Date.now()
    ),
  saveJudges: (key, judges) =>
    writeList({ storage, key: judgesKey(key), list: judges, max: MAX_JUDGES }),
});

export const localValidateStore: ValidateStore = createLocalValidateStore();
